import {AddTeamType} from "../@const"
import Workspace from "../../../model2/space/workspaces/workspace"
import ProjectSession from "../../../model2/project/project-session"
import Task from "../../../model2/task/task"
import TeamsToAdd from "./collection"
import FieldWorkspaceAccess from "../../../fields3/system/project/access/workpace-access"
import {FieldType} from "../../../fields3/field-type"
import FieldProjectAccess from "../../../fields3/system/project/access/project-access"
import {ProjectAccess} from "../../../gd/const/project"
import AddTeam_createTeams from "../actions/create-teams"

// const debug = "AddTeam_Manager"
const debug = false

export type AddTeam_Callback =  {(manager: AddTeam_Manager): void}

export class AddTeam_Manager {

    public type                 : AddTeamType
    public searchPrompt         : string = ''

    public reRender             : callback

    public workspace?           : Workspace         // workspace we add to
    public project?             : ProjectSession    // project we add to
    public task?                : Task              // task we add to

    public workspaceTeamsToAdd  : TeamsToAdd = new TeamsToAdd(this)
    public companyTeamsToAdd    : TeamsToAdd = new TeamsToAdd(this)
    public projectTeamsToAdd    : TeamsToAdd = new TeamsToAdd(this)

    public workspaceAccessField : FieldWorkspaceAccess
    public projectAccessField   : FieldProjectAccess

    public multiselect          : boolean = true
    public noTeamsCreation      : boolean = false   // no requests will be made to create teams in workspace, project or task

    public callback?            : AddTeam_Callback      // called when all done and teams added

    constructor(type:AddTeamType, minProjectAccess?: ProjectAccess) {
        this.type               = type
        this.reRender           = () => { console.warn('Not implemented'); }

        this.workspaceAccessField = new FieldWorkspaceAccess({
            type            : FieldType.WORKSPACE_ACCESS,
            fieldId         : 'workspaceAccess'
        })

        this.projectAccessField = new FieldProjectAccess({
            type            : FieldType.PROJECT_ACCESS,
            fieldId         : 'projectAccess',
            minAccess       : minProjectAccess // minimum project access that can be selected (for example: for Task it can't be lower than 'Limited')
        })
    }

    static initForWorkspace(workspace:Workspace, callback?:callback):AddTeam_Manager {
        const manager       = new AddTeam_Manager(AddTeamType.WORKSPACE_TEAM)

        manager.workspace   = workspace
        manager.callback    = callback

        manager.companyTeamsToAdd.resetForWorkspace(workspace)

        return manager
    }

    static initForProject(project:ProjectSession, callback?:callback):AddTeam_Manager {
        const manager       = new AddTeam_Manager(AddTeamType.PROJECT_TEAM)

        manager.project     = project
        manager.workspace   = project.workspace || undefined
        manager.callback    = callback

        if (manager.workspace) {
            manager.workspaceTeamsToAdd.resetForProjectInWorkspace(manager.workspace, project)
            manager.companyTeamsToAdd.resetForWorkspace(manager.workspace)
        } else {
            manager.companyTeamsToAdd.resetForProjectWithoutWorkspace(project)
            console.error('Not implemented yet #9ed876323')
        }

        return manager
    }

    static initForTask(task:Task, callback?:callback):AddTeam_Manager {
        const manager               = new AddTeam_Manager(AddTeamType.TASK_TEAM)

        const project               = task.project!

        manager.task                = task
        manager.project             = project
        manager.workspace           = project.workspace || undefined
        manager.callback            = callback

        if (manager.workspace) {
            manager.projectTeamsToAdd.resetForTask(task)
            manager.workspaceTeamsToAdd.resetForProjectInWorkspace(manager.workspace, project, task)
            manager.companyTeamsToAdd.resetForWorkspace(manager.workspace, task)
        } else {
            manager.projectTeamsToAdd.resetForTask(task)
            manager.companyTeamsToAdd.resetForProjectWithoutWorkspace(project, task)
        }

        return manager
    }

    get searchEnabled (): boolean {
        return !!this.searchPrompt
    }

    public setSearchPrompt = (prompt:string):void => {
        this.searchPrompt = prompt
        this.reRender()
    }

    get modalTitle():string {
        switch (this.type) {
            case AddTeamType.WORKSPACE_TEAM:
                return "Add Workspace Team"

            case AddTeamType.PROJECT_TEAM:
                return 'Add {0} Team'.f(this.project?.systemTypeName || 'Project')

            case AddTeamType.TASK_TEAM:
                return 'Add Task Team'

            default:
                return 'Add Team'
        }
    }

    get allTeamsToAddSelected() {
        return _.compact([
            ...this.companyTeamsToAdd.selected,
            ...this.workspaceTeamsToAdd.selected,
            ...this.projectTeamsToAdd.selected,
        ])
    }

    unselectAll() {
        this.companyTeamsToAdd.unselectAll()
        this.workspaceTeamsToAdd.unselectAll()
        this.projectTeamsToAdd.unselectAll()
    }

    excludeTeams(excludeTeamIds?: ID[]) {
        if (!excludeTeamIds) return

        _.map(excludeTeamIds, (id: ID)=>{
            this.companyTeamsToAdd.remove(id)
            this.workspaceTeamsToAdd.remove(id)
            this.projectTeamsToAdd.remove(id)
        })
    }

    public submit = async () => {
        // check if users creation requests are needed
        if (!this.noTeamsCreation) {
            await AddTeam_createTeams(this)
        }

        gd.modal.hide()
        this.callback?.(this)
    }
} 